import { useEffect, useRef, useState } from 'react'

import type { ElPeriodo, Periodo } from '@/lib/supabase/tipos-bd'
import { pesos } from '@/shared/utilidades/formato'

import { corto, diaCorto, mesCorto } from './cifras'
import estilos from './CifrasDelPeriodo.module.css'

/**
 * LO ACEPTADO, TRAMO POR TRAMO: día a día en la semana y el mes, mes a mes en
 * el año.
 *
 * Una sola escala —la del tramo más alto—. Los tramos que todavía no llegan
 * van como pista vacía: una barra en cero un jueves que aún no es jueves
 * diría que no se aceptó nada, y eso es falso. Debajo, los servicios
 * finalizados de cada tramo.
 *
 * Con 31 días no caben 31 rótulos: en el mes se rotulan el 1, cada cinco
 * días y el último, y el valor exacto queda al pasar el mouse.
 *
 * SE DIBUJA A LA MEDIDA DE SU CAJA. Con un ancho fijo y escalado, a todo lo
 * ancho de la pantalla quedaba de medio metro de alto, y en el teléfono la
 * letra se volvía de 6 px. Ahora mide su caja y dibuja a esa medida: siempre
 * 200 px de alto y la letra a su tamaño real.
 */
const ALTO = 200
const ARRIBA = 22
const ABAJO = 44

export function GraficaDeTramos({
  tramos,
  periodo,
  hoy,
}: {
  tramos: ElPeriodo['tramos']
  periodo: Periodo
  hoy: string
}) {
  const caja = useRef<HTMLDivElement>(null)
  const [ANCHO, setAncho] = useState(560)
  useEffect(() => {
    const el = caja.current
    if (!el) return
    const medir = () => setAncho(Math.max(240, Math.round(el.clientWidth)))
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(el)
    return () => observador.disconnect()
  }, [])

  const maximo = Math.max(1, ...tramos.map((t) => Number(t.aceptado)))
  const paso = ANCHO / tramos.length
  const anchoBarra = Math.min(46, paso * 0.62)
  const alturaUtil = ALTO - ARRIBA - ABAJO
  const muchos = tramos.length > 12
  const nadaAceptado = tramos.every((t) => Number(t.aceptado) === 0)

  return (
    <div ref={caja}>
      <svg
        width={ANCHO}
        height={ALTO}
        viewBox={`0 0 ${ANCHO} ${ALTO}`}
        className={estilos.grafica}
        role="img"
        aria-label="Cotizaciones aceptadas en el periodo"
      >
        <line x1={0} x2={ANCHO} y1={ALTO - ABAJO} y2={ALTO - ABAJO} className={estilos.ejeBase} />
        {tramos.map((t, i) => {
          const valor = Number(t.aceptado)
          const futuro = t.desde > hoy
          const esHoy = t.desde <= hoy && hoy <= t.hasta
          const alto = futuro ? 0 : Math.max(valor > 0 ? 3 : 0, (valor / maximo) * alturaUtil)
          const x = i * paso + (paso - anchoBarra) / 2
          const y = ALTO - ABAJO - alto
          const { nombre, numero } = diaCorto(t.desde)
          const rotulo =
            periodo === 'año'
              ? mesCorto(t.desde)
              : periodo === 'mes'
              ? numero
              : `${nombre} ${numero}`
          const conRotulo =
            !muchos || i === 0 || i === tramos.length - 1 || (i + 1) % 5 === 0 || esHoy
          return (
            <g key={t.desde}>
              <title>
                {futuro
                  ? `${rotulo}: todavía no llega`
                  : `${rotulo}: aceptado ${pesos(valor)} · cotizado ${pesos(Number(t.cotizado))}${
                      t.finalizados ? ` · ${t.finalizados} finalizados` : ''
                    }`}
              </title>
              <rect
                x={x}
                y={ARRIBA}
                width={anchoBarra}
                height={alturaUtil}
                rx={muchos ? 3 : 6}
                className={estilos.pista}
              />
              {!futuro && (
                <rect
                  x={x}
                  y={y}
                  width={anchoBarra}
                  height={alto}
                  rx={muchos ? 3 : 6}
                  className={esHoy ? estilos.barraHoy : estilos.barra}
                />
              )}
              {!futuro && valor > 0 && !muchos && (
                <text
                  x={x + anchoBarra / 2}
                  y={y - 6}
                  textAnchor="middle"
                  className={estilos.valorBarra}
                >
                  {corto(valor)}
                </text>
              )}
              {conRotulo && (
                <text
                  x={x + anchoBarra / 2}
                  y={ALTO - ABAJO + 20}
                  textAnchor="middle"
                  className={esHoy ? estilos.diaHoy : estilos.dia}
                >
                  {rotulo}
                </text>
              )}
              {t.finalizados > 0 && (
                <circle
                  cx={x + anchoBarra / 2}
                  cy={ALTO - 8}
                  r={muchos ? 3.5 : 5}
                  className={estilos.puntoFinalizado}
                />
              )}
            </g>
          )
        })}
      </svg>
      <p className={estilos.leyenda}>
        <span className={estilos.leyendaBarra} /> Aceptado
        <span className={estilos.leyendaPunto} /> Hubo servicios finalizados
      </p>
      {nadaAceptado && (
        <p className={estilos.vacio}>Ningún cliente aceptó cotizaciones en este periodo.</p>
      )}
    </div>
  )
}
